import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { format, differenceInCalendarDays, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
    CalendarDays, Clock, FileText, ChevronRight, Package, Wrench,
    MessageSquare, CalendarCheck, Download, Paperclip
} from "lucide-react";

import { Camera } from "lucide-react";

const METHOD_LABELS = {
    ASP: "Aspersión", NEB: "Nebulización", GRA: "Granulado", POL: "Polvo",
    GEL: "Gel", GAS: "Gaseado", MIC: "Microencapsulado", CEB: "Cebo",
    TRA: "Trampa", PEG: "Pegante", LAM: "Lámina", KALL: "Kall",
};

const MONTH_LABELS = {
    1: "ENE", 2: "FEB", 3: "MAR", 4: "ABR", 5: "MAY", 6: "JUN",
    7: "JUL", 8: "AGO", 9: "SEP", 10: "OCT", 11: "NOV", 12: "DIC",
};

const DOC_TYPE_LABELS = {
    map_traps: "Mapa de trampas",
    map_bait_stations: "Mapa estaciones de cebo",
    certificate: "Certificado",
    other: "Documento",
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

// Label map for all check keys across sections
const CHECK_LABELS = {
    "exterior.grass_cut": "Césped cortado",
    "exterior.drains_good": "Drenajes y sifones en buen estado",
    "exterior.machinery_organized": "Maquinaria obsoleta ordenada y/o guardada",
    "exterior.debris_organized": "Escombros o materiales ordenados",
    "exterior.grate_screens": "Zaranda en tragantes",
    "exterior.trash_containers_closed": "Contenedores de basura cerrados",
    "exterior.waste_management": "Adecuado manejo de desperdicios",
    "exterior.nesting_sites_found": "Lugares de anidación de plagas (exterior)",
    "interior.walls_clean": "Paredes limpias y sin grietas",
    "interior.floors_clean": "Pisos limpios",
    "interior.ceilings_no_grease": "Techos sin acumulación de grasas",
    "interior.machinery_no_residue": "Maquinaria sin residuos orgánicos",
    "interior.window_screens_good": "Mallas de ventanas en buenas condiciones",
    "interior.doors_sealed": "Puertas cerradas y selladas",
    "interior.general_cleaning_ok": "Limpieza general satisfactoria",
    "interior.nesting_sites_found": "Lugares de anidación de plagas (interior)",
    "food.storage_adequate": "Adecuado almacenamiento de productos",
    "food.surfaces_clean": "Superficies y anexos limpios",
    "food.spaces_behind_clean": "Espacios bajo y detrás limpios",
    "food.all_areas_accessible": "Accesibilidad a todas las áreas",
    "food.structures_good": "Estructuras en buen estado",
    "food.regular_cleaning": "Evidencia de limpieza regular",
    "food.nesting_sites_found": "Lugares de anidación de plagas (bodega)",
};

function ScheduleExportPanel() {
    const today = format(new Date(), "yyyy-MM-dd");
    const firstDayOfMonth = format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd");
    const [fromDate, setFromDate] = useState(firstDayOfMonth);
    const [toDate, setToDate] = useState(today);
    const [completedOnly, setCompletedOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const download = async () => {
        if (!fromDate || !toDate) return setError("Selecciona ambas fechas");
        if (fromDate > toDate) return setError("La fecha de inicio debe ser anterior a la fecha fin");
        setError("");
        setLoading(true);
        try {
            const token = localStorage.getItem("trulyn_token");
            const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
            if (completedOnly) params.set("completed_only", "true");
            const res = await fetch(
                `${BASE_URL}/api/schedules/export-pdf?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || "Error al generar el PDF");
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `programacion_${fromDate}_${toDate}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(e.message || "Error al descargar");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-xl border bg-zinc-50 p-4 space-y-3" data-testid="schedule-export-panel">
            <div className="flex items-center gap-2">
                <Download className="w-4 h-4 text-[#FFD62B]" />
                <p className="text-sm font-bold">Descargar programación</p>
            </div>
            <p className="text-xs text-muted-foreground">Selecciona un rango de fechas para exportar tu programación de servicios en PDF.</p>
            <div className="flex flex-wrap gap-3 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-[11px] text-zinc-500 font-medium">Desde</label>
                    <Input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-40 text-sm"
                        data-testid="export-from-date"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[11px] text-zinc-500 font-medium">Hasta</label>
                    <Input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-40 text-sm"
                        data-testid="export-to-date"
                    />
                </div>
                <div className="flex items-center gap-2 pb-0.5">
                    <Checkbox
                        id="completed-only"
                        checked={completedOnly}
                        onCheckedChange={(v) => setCompletedOnly(!!v)}
                        data-testid="export-completed-only"
                    />
                    <label htmlFor="completed-only" className="text-sm text-zinc-600 cursor-pointer select-none">Solo completados</label>
                </div>
                <Button
                    onClick={download}
                    disabled={loading}
                    className="bg-black hover:bg-zinc-800 text-white gap-2"
                    data-testid="export-download-btn"
                >
                    <Download className="w-4 h-4" />
                    {loading ? "Generando..." : "Descargar PDF"}
                </Button>
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
    );
}

function DateBadge({ dateStr }) {
    const d = parseISO(dateStr + "T00:00:00");
    return (
        <div className="flex flex-col items-center justify-center w-12 h-14 rounded-xl bg-black text-white shrink-0">
            <span className="text-[10px] font-bold tracking-widest text-[#FFD62B]">{MONTH_LABELS[d.getMonth() + 1]}</span>
            <span className="text-xl font-extrabold leading-tight">{d.getDate()}</span>
            <span className="text-[9px] uppercase tracking-wide text-zinc-400">{format(d, "EEE", { locale: es })}</span>
        </div>
    );
}

function CountdownBadge({ dateStr }) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = parseISO(dateStr + "T00:00:00");
    const diff = differenceInCalendarDays(target, today);
    if (diff <= 0) return <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FF0000] bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5">Hoy</span>;
    if (diff === 1) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">Mañana</span>;
    return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 bg-zinc-100 border border-zinc-200 rounded-full px-2.5 py-0.5">
            <Clock className="w-3 h-3" /> En {diff} días
        </span>
    );
}

function ReportDetailDialog({ service, onClose }) {
    const [report, setReport] = useState(null);
    const [docs, setDocs] = useState([]);
    const [firstSeen, setFirstSeen] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!service) return;
        setLoading(true);
        Promise.all([
            api.get("/reports", { params: { scheduled_service_id: service.id } })
                .then((r) => {
                    const match = (r.data || []).find((rep) => rep.scheduled_service_id === service.id);
                    setReport(match || null);
                })
                .catch(() => setReport(null)),
            api.get("/documents", { params: { scheduled_service_id: service.id } })
                .then((r) => setDocs(r.data || []))
                .catch(() => setDocs([])),
            api.get("/reports/check-first-seen")
                .then((r) => setFirstSeen(r.data || {}))
                .catch(() => setFirstSeen({})),
        ]).finally(() => setLoading(false));
    }, [service]);

    if (!service) return null;
    const d = parseISO(service.scheduled_date + "T00:00:00");
    const title = `${format(d, "EEEE d 'de' MMMM yyyy", { locale: es })}`;

    return (
        <Dialog open={!!service} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="report-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 capitalize text-base">
                        <CalendarCheck className="w-4 h-4 text-[#FF0000]" />
                        {title}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusPill status={service.status} />
                    {service.service_type_flag && (
                        <span className="text-xs font-semibold text-zinc-700 bg-zinc-100 rounded px-2 py-0.5">{service.service_type_flag}</span>
                    )}
                    {service.scheduled_time && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" /> {service.scheduled_time}
                        </span>
                    )}
                    {service.order_number && (
                        <span className="text-xs font-mono text-muted-foreground">#{service.order_number}</span>
                    )}
                </div>

                {loading && <p className="text-sm text-muted-foreground py-4 text-center">Cargando...</p>}

                {!loading && !report && docs.length === 0 && (
                    <Card className="p-4 text-sm text-muted-foreground text-center bg-zinc-50">
                        No hay reporte disponible para este servicio.
                    </Card>
                )}

                {!loading && report && (
                    <div className="space-y-4 mt-1">
                        {/* Areas treated */}
                        {report.areas_treated && (
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
                                    <Wrench className="w-3 h-3" /> Áreas tratadas
                                </p>
                                <p className="text-sm leading-relaxed">{report.areas_treated}</p>
                            </div>
                        )}

                        {/* Recommendations */}
                        {report.recommendations && (
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" /> Recomendaciones
                                </p>
                                <p className="text-sm leading-relaxed">{report.recommendations}</p>
                            </div>
                        )}

                        {/* Corrective actions */}
                        {report.corrective_actions && (
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1 flex items-center gap-1">
                                    <Wrench className="w-3 h-3" /> Acciones correctivas
                                </p>
                                <p className="text-sm leading-relaxed">{report.corrective_actions}</p>
                            </div>
                        )}

                        {/* Products used */}
                        {report.products_used?.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
                                    <Package className="w-3 h-3" /> Productos utilizados
                                </p>
                                <div className="space-y-1.5">
                                    {report.products_used.map((p, i) => (
                                        <div key={i} className="flex items-center justify-between rounded-lg border bg-zinc-50 px-3 py-2 text-sm">
                                            <span className="font-medium">{p.product_name}</span>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <span>{p.quantity} {p.unit || ""}</span>
                                                {p.method && <Badge variant="outline" className="text-[10px] px-1 py-0">{METHOD_LABELS[p.method] || p.method}</Badge>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Follow-up — only if actually scheduled */}
                        {report.follow_up_scheduled === true && (
                            <div className="rounded-lg bg-[#FFD62B]/10 border border-[#FFD62B]/30 px-3 py-2.5">
                                <p className="text-xs font-semibold text-black mb-0.5">Visita de refuerzo programada</p>
                                {report.follow_up_date && (
                                    <p className="text-sm font-bold">{report.follow_up_date}</p>
                                )}
                            </div>
                        )}

                        {/* Reincident observations */}
                        {report.reincident_checks?.length > 0 && (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                                <p className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2 flex items-center gap-1.5">
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-600" />
                                    Observaciones reincidentes ({report.reincident_checks.length})
                                </p>
                                <ul className="space-y-2">
                                    {report.reincident_checks.map((key) => {
                                        const label = CHECK_LABELS[key] || key;
                                        const first = firstSeen[key];
                                        const isThisReport = first === report.service_date;
                                        return (
                                            <li key={key} className="flex flex-col gap-0.5">
                                                <span className="text-xs font-semibold text-red-800">{label}</span>
                                                {first && !isThisReport ? (
                                                    <span className="text-[10px] text-red-500">
                                                        Primera vez reportado: <strong>{first}</strong>
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-red-400 italic">Primera vez detectado en este servicio</span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        {/* PDF link */}
                        {report.report_pdf_url && (
                            <Button
                                className="w-full bg-black hover:bg-zinc-800 text-white"
                                onClick={() => window.open(`${BASE_URL}${report.report_pdf_url}`, "_blank")}
                                data-testid="report-pdf-button"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Descargar reporte PDF
                            </Button>
                        )}
                    </div>
                )}

                {/* Attached documents section */}
                {!loading && docs.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
                            <Paperclip className="w-3 h-3" /> Documentos adjuntos
                        </p>
                        <div className="space-y-2">
                            {docs.map((doc) => (
                                <a
                                    key={doc.id}
                                    href={`${BASE_URL}${doc.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between rounded-lg border bg-zinc-50 hover:bg-zinc-100 px-3 py-2.5 transition-colors group"
                                    data-testid={`service-doc-${doc.id}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{doc.title}</p>
                                            <p className="text-[10px] text-zinc-400">{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</p>
                                        </div>
                                    </div>
                                    <Download className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-700 shrink-0 ml-2" />
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ServiceRow({ s, onSelect, mipPdfUrl }) {
    const isCompleted = s.status === "completed";
    const todayStr = format(new Date(), "yyyy-MM-dd");
    const isPastItem = s.scheduled_date < todayStr || isCompleted;
    const isUpcoming = s.scheduled_date >= todayStr && !isCompleted;

    const handleMipDownload = (e) => {
        e.stopPropagation();
        window.open(`${BASE_URL}${mipPdfUrl}`, "_blank");
    };

    return (
        <div
            className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${isCompleted ? "cursor-pointer hover:bg-zinc-50 hover:border-zinc-300" : ""}`}
            onClick={() => isCompleted && onSelect(s)}
            data-testid={`service-row-${s.id}`}
        >
            <DateBadge dateStr={s.scheduled_date} />
            <div className="flex-1 min-w-0">
                {s.service_type_flag && (
                    <p className="text-sm font-semibold text-zinc-800 leading-tight truncate mb-0.5">{s.service_type_flag}</p>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                    {s.scheduled_time && (
                        <span className="text-xs font-semibold text-zinc-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {s.scheduled_time}
                        </span>
                    )}
                    {s.order_number && <span className="text-xs font-mono text-zinc-400">#{s.order_number}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <StatusPill status={s.status} />
                    {s.invoice_type && <span className="text-[11px] text-zinc-400 font-mono">{s.invoice_type}</span>}
                </div>
            </div>

            {/* Right side — countdown for upcoming, actions for completed */}
            <div className="shrink-0 flex items-center gap-1.5">
                {isUpcoming && <CountdownBadge dateStr={s.scheduled_date} />}
                {isCompleted && (
                    <>
                        {mipPdfUrl && (
                            <button
                                className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg px-2.5 py-1.5 transition-colors"
                                onClick={handleMipDownload}
                                data-testid={`service-row-mip-${s.id}`}
                            >
                                <Camera className="w-3 h-3" />
                                Reporte MIP
                            </button>
                        )}
                        <button
                            className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 rounded-lg px-2.5 py-1.5 transition-colors"
                            onClick={(e) => { e.stopPropagation(); onSelect(s); }}
                            data-testid={`service-row-view-report-${s.id}`}
                        >
                            <FileText className="w-3 h-3" />
                            Ver reporte
                        </button>
                    </>
                )}
                {!isCompleted && !isUpcoming && (
                    <ChevronRight className="w-4 h-4 text-zinc-300" />
                )}
            </div>
        </div>
    );
}

export default function PortalCalendar() {
    const { branchParam } = useBranch();
    const [services, setServices] = useState([]);
    const [mipMap, setMipMap] = useState({}); // { service_id: pdf_url }
    const [loading, setLoading] = useState(true);
    const [selectedService, setSelectedService] = useState(null);
    const todayISO = format(new Date(), "yyyy-MM-dd");

    useEffect(() => {
        Promise.all([
            api.get("/schedules", { params: branchParam }).then((r) => setServices(r.data || [])),
            api.get("/photo-reports/client/completed")
                .then((r) => {
                    const map = {};
                    (r.data || []).forEach((pr) => { if (pr.pdf_url) map[pr.service_id] = pr.pdf_url; });
                    setMipMap(map);
                })
                .catch(() => {}),
        ]).finally(() => setLoading(false));
    }, [branchParam]);

    const upcoming = services
        .filter((s) => s.scheduled_date >= todayISO && s.status !== "completed")
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

    const history = services
        .filter((s) => s.scheduled_date < todayISO || s.status === "completed")
        .sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date) || (b.scheduled_time || "").localeCompare(a.scheduled_time || ""));

    if (loading) return (
        <div className="space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-zinc-100" />)}
        </div>
    );

    return (
        <div data-testid="portal-calendar" className="space-y-8 pb-8">
            <div>
                <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Mis servicios</h1>
                <p className="text-sm text-muted-foreground">Historial y próximas visitas programadas</p>
            </div>

            <ScheduleExportPanel />

            {/* Upcoming services */}
            {upcoming.length > 0 && (
                <section>
                    <div className="flex items-center gap-2 mb-3">
                        <CalendarDays className="w-4 h-4 text-[#FFD62B]" />
                        <h2 className="text-sm font-bold uppercase tracking-wider">Próximos servicios</h2>
                        <Badge className="bg-[#FFD62B] text-black font-bold text-[11px]">{upcoming.length}</Badge>
                    </div>
                    <div className="space-y-2">
                        {upcoming.map((s) => <ServiceRow key={s.id} s={s} onSelect={setSelectedService} mipPdfUrl={mipMap[s.id]} />)}
                    </div>
                </section>
            )}

            {/* Divider — today */}
            <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-2">Hoy</span>
                <div className="flex-1 h-px bg-border" />
            </div>

            {/* Service history */}
            <section>
                <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-zinc-400" />
                    <h2 className="text-sm font-bold uppercase tracking-wider">Historial de servicios</h2>
                    <Badge variant="outline" className="font-bold text-[11px]">{history.length}</Badge>
                </div>
                {history.length === 0 && (
                    <Card className="p-6 text-center text-sm text-muted-foreground bg-zinc-50">
                        Aún no hay servicios realizados.
                    </Card>
                )}
                <div className="space-y-2">
                    {history.map((s) => <ServiceRow key={s.id} s={s} onSelect={setSelectedService} mipPdfUrl={mipMap[s.id]} />)}
                </div>
            </section>

            {upcoming.length === 0 && history.length === 0 && (
                <Card className="p-8 text-center text-muted-foreground">
                    <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No hay servicios programados.</p>
                </Card>
            )}

            <ReportDetailDialog service={selectedService} onClose={() => setSelectedService(null)} />
        </div>
    );
}
